import { IconButton } from 'components/Buttons/IconButton';
import { ButtonLink } from 'components/Buttons/ButtonLink';
import {
  followUser,
  unfollowUser
} from 'features/current-user/currentUserSlice';
import React, { FunctionComponent, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCurrentUser } from 'features/current-user/hooks';
import { showAuthenticationEmailPromptForm } from 'features/current-user/currentUserSlice';
import { RootState } from 'rootReducer';
import { UserAgentContext } from 'features/user-agent/userAgent';
import { useSocialElementsDisabled } from 'features/channel/channelHooks';

export interface FollowButtonsProps {
  selectedProfileId: string;
  followedIds: string[];
  showAslink?: boolean;
}

export const FollowButtons: FunctionComponent<FollowButtonsProps> = ({
  followedIds,
  selectedProfileId,
  showAslink
}: FollowButtonsProps) => {
  const isFollowing: boolean = followedIds.includes(selectedProfileId);
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const channel = useSelector(
    (state: RootState) => state.channel.channelDetails
  );
  const { isListenerAppWebview, isEmbed } = useContext(UserAgentContext);
  const socialElementsDisabled = useSocialElementsDisabled();

  const onClick = () => {
    if (currentUser) {
      dispatch(
        isFollowing
          ? unfollowUser(selectedProfileId)
          : followUser(selectedProfileId)
      );
    } else {
      if (isListenerAppWebview) {
        window.location.href = `mixlr://auth?url=${
          import.meta.env.VITE_WEBSITE_ENDPOINT
        }/signin`;
      } else {
        dispatch(
          showAuthenticationEmailPromptForm(
            `To follow ${channel && channel.username}`
          )
        );
      }
    }
  };

  if (socialElementsDisabled) {
    return null;
  }

  if (isEmbed) {
    return (
      <IconButton
        showLabel={true}
        icon={'userAdd'}
        label={'Follow'}
        href={window.location.origin}
        external={true}
        noBackground={true}
        target="_blank"
        rel="noopener noreferrer"
      />
    );
  }

  if (showAslink) {
    return (
      <ButtonLink
        label={isFollowing ? 'Following' : 'Follow'}
        icon={isFollowing ? 'userTick' : 'userAdd'}
        onClick={onClick}
        iconSize={14}
      />
    );
  }

  return (
    <IconButton
      label={isFollowing ? 'Following' : 'Follow'}
      onClick={onClick}
      showLabel={true}
      size={'mini'}
      icon={isFollowing ? 'userTick' : 'userAdd'}
      isHighlighted={isFollowing}
    />
  );
};
